import { Plus } from 'lucide-react'
import type { ParseKeys } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { Checkbox, DatePicker, Field, Select, Textarea } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Sheet } from '@/components/ui/Sheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { useSession } from '@/features/auth/api/useSession'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { CounterpartyPicker } from '@/features/counterparties/components/CounterpartyPicker'
import {
  COUNTERPARTY_CATEGORIES,
  counterpartyRights,
} from '@/features/counterparties/counterpartyList'
import { useTransportRefs } from '@/features/directories/api/useDirectory'
import { useDirectionTemplates } from '@/features/settings/api/useDirectionTemplates'
import { modeFromCode, transportModeLabel } from '@/lib/icons'
import { formatMoney } from '@/lib/format'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import { isSuppressed } from '@/types/domain'
import type {
  CaseLeg,
  DirectionTemplate,
  ExpenseLine,
  LegStatus,
  Maybe,
} from '@/types/domain'

/* The case-detail editors: the expense sheet, and the leg's FIELD GROUP for
 * the two surfaces that are no longer sheets at all. (The participant sheet went
 * with the case's participants, S-22a.)
 *
 * The sheets are what Operations touches daily, so they follow the same
 * shape as every other sheet in the product rather than inventing a
 * faster-looking one. Speed here comes from the sheet being the same sheet —
 * the fields are where they were last time — not from a bespoke inline grid per
 * section.
 *
 * A LEG is not among them any more. Amending one moved to `/cases/:id/legs/:id`
 * when a direction acquired a route chain (issue #35 story 3), and creating one
 * followed it to `/cases/:id/legs/new`: a 440px modal over a list could hold
 * neither the chain nor the trucks nor the money, and a create that opened in a
 * sheet while the record it made lived on a page was the last surface still
 * saying a direction is a dialog. What stayed here is `LegFields` — the one
 * definition of what a leg is made of, shared by both pages, because the PUT
 * behind them replaces the WHOLE record and a second copy of this markup would
 * quietly clear whatever it forgot to render.
 */

/** A suppressed value never loads into an editable input (AD-6). */
function editableValue(value: Maybe<string> | undefined): string {
  if (value === undefined || isSuppressed(value)) return ''
  return value
}

function isReadOnly(value: Maybe<string> | undefined): boolean {
  return isSuppressed(value)
}

/** Accepts `1234.56`, `1 234,56`; rejects text and blanks. Money is a STRING. */
function isValidAmount(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised !== '' && /^-?\d+(\.\d{1,2})?$/.test(normalised)
}

const LEG_STATUSES: LegStatus[] = [
  'planned',
  'in-transit',
  'waiting-border',
  'arrived',
  'completed',
]

/* ── The delete row, once ──────────────────────────────────────────────────── */

/** The dangerous action, below the fields and behind two presses.
 *
 *  ONE implementation for every sheet in this file that deletes, so the
 *  confirm-reset bug is fixed in one place — and that bug is subtle enough to be
 *  worth fixing once: a half-pressed delete must not survive the sheet closing,
 *  or reopening on another record puts the dangerous button under a click meant
 *  for something else.
 *
 *  Reset by KEY during render rather than in an effect, the pattern
 *  `useFormDraft` and `DatePicker` document: FormSheet keeps its markup mounted
 *  (Base UI `keepMounted`), so nothing unmounts to clear this, and an effect
 *  would paint one frame with the confirmation already showing.
 *
 *  Below the fields and separated by a rule rather than beside Save, so the
 *  dangerous action is off the same muscle memory as the safe one. */
function DeleteRow({
  onDelete,
  pending,
  resetKey,
  label,
  confirm,
  confirmAction,
}: {
  onDelete: () => void
  pending?: boolean
  /** Identifies the record the confirmation belongs to. */
  resetKey: string | null
  label: string
  confirm: string
  confirmAction: string
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [confirmedFor, setConfirmedFor] = useState<string | null>(resetKey)

  if (confirmedFor !== resetKey) {
    setConfirmedFor(resetKey)
    setConfirming(false)
  }

  return (
    <div className="border-line-hair mt-1 border-t pt-3">
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text-body flex-1 text-[12.5px] leading-snug">
            {confirm}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirming(false)}
            disabled={pending}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button variant="danger" size="sm" onClick={onDelete} disabled={pending}>
            {confirmAction}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={pending}
          className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {label}
        </button>
      )}
    </div>
  )
}

/* ── Leg ──────────────────────────────────────────────────────────────────── */

export interface LegDraft {
  origin: string
  destination: string
  /* ISO 3166-1 alpha-2 beside each place, never instead of it. The quote's
     route form made the opposite choice — its endpoint field IS the country —
     and a leg deliberately keeps both: "Khorgos" is where the cargo physically
     sits, and "KZ" is what a corridor report groups by. */
  originCountry: string
  destinationCountry: string
  /* The directory row's id, not a mode CODE. The server takes a
     `transportModeId` because a leg's mode is a reference into transport_refs,
     which the desk maintains (FR-43) — a code would be this form inventing a
     vocabulary the directory already owns. */
  transportModeId: string
  status: LegStatus
  /* Whether the cargo changes vehicle on this leg (migration 0062). */
  transshipment: boolean
  /* The carrier as BOTH: the directory link the picker sets, and the name.
     The name is not redundant — a leg linked to nobody keeps what was typed off
     its waybill, and re-saving such a leg after an unrelated status change must
     not erase it. See `toLegPayload`. */
  carrierId: string
  carrierName: string
  reference: string
  /* The conveyance — a flight number on air, a voyage on sea (FR-26). Its own
     field because an air leg has BOTH this and a waybill, and one input for
     the pair would make the desk choose which fact to keep. */
  voyageNumber: string
  /* No units (S-18b). A truck used to be a `LegVehicleDraft` here — the WHOLE
     row, because a payload of `[{plate}]` blanked the other seven fields of
     every truck under a save that reported success — and the containers a
     repeater of number/seal/type. Both are units now, entered only on the leg
     page's unit cards and sent beside this draft with their version
     (`LegUnitsWrite`), so a payload built from it carries no units key and
     the PUT keeps the stored ones. */
  etd: string
  eta: string
  /* The four facts that only exist once the leg has MOVED (issue #35 story 3).
     Optional rather than required, and that asymmetry is deliberate: the CREATE
     sheet does not ask them — an actual departure on a leg nobody has entered
     yet is not a fact anybody holds — while the leg page, which edits a leg
     that is running, asks all four. `toLegPayload` reads an absent one as
     "nobody stated this", which is what every other optional on that payload
     means. */
  atd?: string
  ata?: string
  stallNote?: string
  lastLocation?: string
}

/** A group of `LegFields` — one tab of the leg page's drawer (S-10). */
export type LegFieldSection = 'transport' | 'documents' | 'dates' | 'status'

/** Every field a leg carries, as controls.
 *
 *  Extracted from the sheet when the leg got a PAGE (issue #35 story 3): the
 *  page edits the same record with the same whole-record PUT, and two copies of
 *  this markup would be two answers to what a leg is made of — with the second
 *  copy quietly clearing whatever it forgot to render, because the PUT replaces
 *  everything it is not sent.
 *
 *  The container decides the frame; this decides the fields. */
export function LegFields({
  draft,
  setDraft,
  touched,
  record,
  actuals,
  lockedEnds,
  section,
  readOnly,
}: {
  draft: LegDraft
  setDraft: (next: LegDraft) => void
  /** Whether the surface has been submitted once — errors stay quiet until it
   *  has, so a blank form does not open shouting. */
  touched: boolean
  /** The leg being edited, read ONLY for the suppression the carrier control
   *  honours (AD-6). Absent when creating. */
  record?: CaseLeg
  /** Also ask the facts that only exist once the leg is running: the actual
   *  dates, where it was last seen, and why it is standing still. Off on the
   *  create sheet, where none of them can be true yet. */
  actuals?: boolean
  /** Why the two ends are SHOWN rather than asked — a sentence, or absent.
   *
   *  The create surface passes one once a direction template is chosen, because
   *  the ends are then not this form's to decide: applying a corridor calls
   *  `SetLegEnds` in the same transaction and overwrites both of them with the
   *  chain's first and last point. Leaving the controls live meant the reader
   *  typed four fields that were silently replaced a second later, which is not
   *  a validation problem — it is a form asking a question whose answer it
   *  throws away. */
  lockedEnds?: string
  /** One group of the fields rather than all of them — a tab of the leg
   *  page's «Параметры плеча» drawer (S-10): `transport` (mode,
   *  transshipment), `documents` (the document number, the conveyance), `dates`
   *  (the plan, and the actuals with `actuals`), `status` (status, last
   *  location, stall note). A section draws no ends — the drawer's «Маршрут»
   *  is the chain, whose first and last point ARE the ends — and no carrier,
   *  which «Плечо целиком» and the unit cards carry as the default for new units. Nothing
   *  folds inside a section: the tab is already the disclosure. */
  section?: LegFieldSection
  /** Every control shown and none editable — a reader without
   *  `case.route.edit`, or a case whose status refuses route edits. */
  readOnly?: boolean
}) {
  const { t } = useTranslation()
  const { data: modes } = useTransportRefs('mode', true)
  const shows = (group: LegFieldSection) => section === undefined || section === group

  /* No complaint about an end the reader was not allowed to type. The values are
     the template's and the server will re-derive them anyway. */
  const originError =
    touched && !lockedEnds && !draft.origin.trim() ? t('common.form.required') : undefined
  const destinationError =
    touched && !lockedEnds && !draft.destination.trim()
      ? t('common.form.required')
      : undefined
  /* Required here and required on the server: unlike a case header, which the
     desk opens before the routing is decided, a LEG is a movement — and a
     movement with no mode is not one. */
  const modeError =
    touched && !draft.transportModeId ? t('common.form.required') : undefined

  /* The voyage number on a ROAD or RAIL leg (QW-8, D-3). A truck has none,
     and asking for it on every road leg was a question the desk skipped every
     time — so while the leg holds none, in the draft or on the saved record,
     it sits behind one control. Every other mode is unchanged. The containers
     used to fold beside it; since S-18b they are units, entered on the leg
     page's «Контейнеры» card, and a road leg holding one still unfolds the
     voyage field (the latch reads the saved container units).

     Two rules keep the fold from losing anything. A value is never hidden:
     anything typed or saved shows its field. And the fields never fold back
     once out — the latch below is set by the control AND by a value appearing,
     so clearing a voyage typed before the mode became road does not take the
     field, and the cursor, away mid-edit. Values live in the draft throughout;
     the fold only decides what is drawn. The latch is KEYED by the saved leg
     (`'new'` on the create desk), as the unit cards key their carrier
     override: the leg page stays mounted across a `legId` change, and a latch
     carried over would unfold the next road leg for no visible reason.

     The mode is classified through `modeFromCode`, which ignores the
     catalogue's case — fixtures and desk-added rows do not agree on it. */
  const conveyance = modeFromCode(
    modes?.find((mode) => mode.id === draft.transportModeId)?.code,
  )
  const holdsConveyance =
    draft.voyageNumber !== '' ||
    Boolean(record?.voyageNumber) ||
    (record?.units ?? []).some((unit) => unit.kind === 'container')
  const latchKey = record?.id ?? 'new'
  const [revealedFor, setRevealedFor] = useState<string | null>(null)
  if (holdsConveyance && revealedFor !== latchKey) setRevealedFor(latchKey)
  const folded = section === undefined && revealedFor !== latchKey && !holdsConveyance
  const voyageFolded = folded && (conveyance === 'road' || conveyance === 'rail')

  return (
    <>
      {/* The route, as a PLACE and a COUNTRY per end.
       *
       * Two fields rather than the quote route form's one, and the difference
       * is deliberate rather than an inconsistency: `RouteOptionsCard` stores
       * the country IN the endpoint, so a saved option reads "CN → KZ → TM". A
       * leg endpoint is a terminal, a siding or a yard whose name Operations
       * types off a carrier's email, and "Khorgos" is the fact the desk needs
       * on the phone — the country is a second, structured fact for the reports
       * that group by corridor.
       *
       * The country control is the flag ALONE once chosen, and it LEADS the
       * row. A country narrows the place, so it is answered first and read
       * first — the same order a customs line takes ("KZ, Khorgos") and the
       * order the leg card already renders. A second full-width control
       * repeating "Kazakhstan" would compete with "Khorgos" for the eye on a
       * row that is really one fact, so it stays the flag alone. Its name is
       * still spoken (CountryDropdown composes the accessible name) and still
       * labels every row in the list.
       *
       * STACKED below `sm`. Two flag-plus-place pairs side by side do not fit
       * a phone's card: at 390 px «Куда» was clipped at its right edge. From
       * `sm` up they share the row, as before. */}
      {section === undefined ? (
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <Field
          label={t('quotes.form.origin')}
          required
          error={originError}
          hint={lockedEnds}
          className="flex-1"
        >
          {(props) => (
            <div className="flex items-start gap-1.5">
              <CountryDropdown
                flagOnly
                disabled={lockedEnds !== undefined}
                value={draft.originCountry}
                onChange={(originCountry) => setDraft({ ...draft, originCountry })}
                aria-label={t('cases.legs.originCountry')}
                className="w-auto shrink-0 px-2.5"
              />
              <Input
                disabled={lockedEnds !== undefined}
                value={draft.origin}
                onChange={(e) => setDraft({ ...draft, origin: e.target.value })}
                className="min-w-0 flex-1"
                {...props}
              />
            </div>
          )}
        </Field>

        <Field
          label={t('quotes.form.destination')}
          required
          error={destinationError}
          hint={lockedEnds}
          className="flex-1"
        >
          {(props) => (
            <div className="flex items-start gap-1.5">
              <CountryDropdown
                flagOnly
                disabled={lockedEnds !== undefined}
                value={draft.destinationCountry}
                onChange={(destinationCountry) =>
                  setDraft({ ...draft, destinationCountry })
                }
                aria-label={t('cases.legs.destinationCountry')}
                className="w-auto shrink-0 px-2.5"
              />
              <Input
                disabled={lockedEnds !== undefined}
                value={draft.destination}
                onChange={(e) => setDraft({ ...draft, destination: e.target.value })}
                className="min-w-0 flex-1"
                {...props}
              />
            </div>
          )}
        </Field>
      </div>
      ) : null}

      {shows('transport') ? (
      <Field label={t('quotes.form.mode')} required error={modeError}>
        {(props) => (
          <Select
            value={draft.transportModeId}
            onChange={(transportModeId) => setDraft({ ...draft, transportModeId })}
            options={(modes ?? []).map((mode) => ({
              value: mode.id,
              // The catalogue's word for a code it knows, and the directory's
              // own label for one the desk added (FR-43) that no key covers.
              label: transportModeLabel(mode, t),
              hint: mode.code,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            disabled={readOnly}
            {...props}
          />
        )}
      </Field>
      ) : null}

      {shows('status') ? (
      <Field label={t('common.fields.status')} required>
        {(props) => (
          <Select
            value={draft.status}
            onChange={(status) => setDraft({ ...draft, status })}
            options={toOptions(LEG_STATUSES, (status) => t(`legStatus.${status}`))}
            disabled={readOnly}
            {...props}
          />
        )}
      </Field>
      ) : null}

      {shows('transport') ? (
      <Checkbox
        label={t('routePoints.transshipment')}
        checked={draft.transshipment}
        disabled={readOnly}
        onCheckedChange={(transshipment) => setDraft({ ...draft, transshipment })}
      />
      ) : null}

      {/* The carrier, picked from the directory (FR-40) rather than typed.
       *
       * Still suppressed for Sales exactly as the free-text field was, and now
       * on BOTH halves: the server withholds the id under the same call as the
       * name, so a caller who may not read who carried a leg cannot read the
       * key that resolves it either. The control is disabled rather than
       * hidden, so the field still says the fact exists and is withheld
       * (AD-6) — and `editableValue` above already refused to load a
       * suppression marker into the draft, so a re-enabled control still could
       * not write one back. */}
      {section === undefined ? (
      <Field
        label={t('cases.legs.carrier')}
        hint={isReadOnly(record?.carrierName) ? t('common.suppressed.reason') : undefined}
      >
        {(props) => (
          <CarrierSelect
            value={draft.carrierId}
            name={draft.carrierName}
            disabled={isReadOnly(record?.carrierName)}
            onChange={(carrier) =>
              setDraft({ ...draft, carrierId: carrier.id, carrierName: carrier.name })
            }
            {...props}
          />
        )}
      </Field>
      ) : null}

      {/* Named by the paper the mode travels on (S-8, O2) — the split the leg
          page's header makes. «Номер документа» while no mode is picked, and
          for a mode code this client does not know: the header calls that
          paper «Накладная», but the form does not guess. On the road the hint
          says where the OTHER «№ CMR» lives — each truck has its own on «Фуры
          и ставки», and two inputs under one name on one page need telling
          apart. */}
      {shows('documents') ? (
      <Field
        label={t(
          conveyance === 'road'
            ? 'cases.legs.referenceCmr'
            : conveyance
              ? 'cases.legs.referenceWaybill'
              : 'cases.legs.reference',
        )}
        hint={
          conveyance === 'road'
            ? `${t('common.form.optional')} · ${t('cases.legs.referenceCmrHint')}`
            : t('common.form.optional')
        }
      >
        {(props) => (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            className="font-mono"
            disabled={readOnly}
            {...props}
          />
        )}
      </Field>
      ) : null}

      {/* The conveyance (FR-26). Beneath the waybill rather than beside it,
          because they are read together and an air leg fills in both — putting
          them on one row would suggest choosing between them.

          The LABEL follows the mode: a rail leg has no voyage number and a sea
          leg has no flight, and one label reading "flight / voyage number"
          would make the desk pause on every leg to work out which half applies
          to them. */}
      {!shows('documents') ? null : voyageFolded ? (
        <Button
          variant="ghost"
          size="sm"
          iconLeft={Plus}
          className="self-start"
          onClick={() => setRevealedFor(latchKey)}
        >
          {/* Named for what it opens — the voyage alone, on road and on rail
              (the containers are a unit card since S-18b). */}
          {t('cases.legs.revealVoyage')}
        </Button>
      ) : (
        <Field
          label={t(
            conveyance === 'air' ? 'cases.legs.flightNumber' : 'cases.legs.voyageNumber',
          )}
          hint={t('common.form.optional')}
        >
          {(props) => (
            <Input
              value={draft.voyageNumber}
              onChange={(e) => setDraft({ ...draft, voyageNumber: e.target.value })}
              className="font-mono"
              disabled={readOnly}
              {...props}
            />
          )}
        </Field>
      )}

      {/* No units here (S-8, S-18b). Trucks, containers and every other unit
          are entered on the leg page's unit cards, which have the width for a
          unit's facts; a plate repeater, and later the containers repeater,
          on this form were a second, poorer place to type the same number.
          The leg page's PUT carries the units only when its cards were
          touched, always with the version of the list the rows were edited
          from (`legUpdatePayload`, `CaseLegPage`). */}

      {shows('dates') ? (
      <div className="flex gap-2.5">
        {/* The header's words for the four dates (S-8), not the trade's
            letters: the page prints «План отправления» above this card, and a
            form calling the same date ETD read as a second fact. The case
            board, list and form keep ETD/ETA on purpose. */}
        <Field label={t('cases.legPage.dateLabels.etd')} className="flex-1">
          {(props) => (
            <DatePicker
              value={draft.etd}
              onChange={(etd) => setDraft({ ...draft, etd })}
              disabled={readOnly}
              {...props}
            />
          )}
        </Field>

        {/* A leg cannot arrive before it departs. */}
        <Field label={t('cases.legPage.dateLabels.eta')} className="flex-1">
          {(props) => (
            <DatePicker
              value={draft.eta}
              min={draft.etd || undefined}
              onChange={(eta) => setDraft({ ...draft, eta })}
              disabled={readOnly}
              {...props}
            />
          )}
        </Field>
      </div>
      ) : null}

      {/* What actually happened, asked only where it can have happened.
       *
       * These four travelled to the browser on every leg read and no form asked
       * for any of them: the ATD and ATA were writable only by whatever wrote
       * the fixture, and the stall note the case screen PRINTS in an amber
       * banner could not be typed anywhere in the application. A whole-record
       * PUT that never sends a field is also a PUT that clears it, so the
       * omission was not merely a gap — it was a form that quietly dropped the
       * dispatcher's note the next time somebody corrected a date. */}
      {actuals && shows('dates') ? (
          <div className="flex gap-2.5">
            <Field label={t('cases.legPage.dateLabels.atd')} className="flex-1">
              {(props) => (
                <DatePicker
                  value={draft.atd ?? ''}
                  onChange={(atd) => setDraft({ ...draft, atd })}
                  disabled={readOnly}
                  {...props}
                />
              )}
            </Field>

            <Field label={t('cases.legPage.dateLabels.ata')} className="flex-1">
              {(props) => (
                <DatePicker
                  value={draft.ata ?? ''}
                  /* A leg cannot arrive before it left, in fact any more than
                     in plan — and the actual departure is the floor here, not
                     the planned one. */
                  min={draft.atd || undefined}
                  onChange={(ata) => setDraft({ ...draft, ata })}
                  disabled={readOnly}
                  {...props}
                />
              )}
            </Field>
          </div>
      ) : null}

      {/* Where the leg was last seen and why it stands. On the whole form only
          once the leg is running (`actuals`); on the drawer's «Статус» tab
          always — the tab is where a movement is reported. */}
      {(section === undefined && actuals) || section === 'status' ? (
        <>
          <Field
            label={t('cases.legs.lastLocation')}
            hint={t('cases.legPage.lastLocationHint')}
          >
            {(props) => (
              <Input
                value={draft.lastLocation ?? ''}
                onChange={(e) => setDraft({ ...draft, lastLocation: e.target.value })}
                disabled={readOnly}
                {...props}
              />
            )}
          </Field>

          <Field label={t('cases.legPage.stallNote')} hint={t('common.form.optional')}>
            {(props) => (
              <Input
                value={draft.stallNote ?? ''}
                onChange={(e) => setDraft({ ...draft, stallNote: e.target.value })}
                disabled={readOnly}
                {...props}
              />
            )}
          </Field>
        </>
      ) : null}
    </>
  )
}

/** The direction a NEW leg's chain is filled from (issue #35 story 3).
 *
 *  Offered on creation only, which is why it is not part of `LegFields`:
 *  applying a direction OVERWRITES the chain, and a picker sitting on an amend
 *  form would put that overwrite one stray click away from a leg whose arrivals
 *  somebody has been recording for a fortnight. On the leg's own page it is a
 *  deliberate action behind a warning instead.
 *
 *  Optional, and the hint says what choosing one does: the points are COPIED,
 *  so the corridor can be edited in Settings afterwards without moving this
 *  shipment.
 *
 *  Its own component rather than markup on the create page, for `LegFields`'
 *  reason: this control and the leg it belongs to are one form, and the day a
 *  second surface offers a template on a create it must offer the same list
 *  with the same warning in it. */
export function DirectionTemplateField({
  value,
  onChange,
}: {
  value: string
  /** The chosen corridor, and the RECORD beside its id.
   *
   *  The record travels because the caller needs the two ends off it — the
   *  chain's first and last point become the leg's own, so a create that made
   *  the reader type them was asking for four values the apply overwrites. It is
   *  handed over rather than re-fetched: this component already holds the list,
   *  and a second `useDirectionTemplates` at the call site would be a second
   *  cache entry that can answer differently. */
  onChange: (templateId: string, template?: DirectionTemplate) => void
}) {
  const { t } = useTranslation()
  /* Only the LIVE directions: Settings keeps retired corridors maintainable,
     and a picker that offered them would fill a new leg from a route the desk
     has decided is no longer run. */
  const { data: directions } = useDirectionTemplates({ activeOnly: true })

  return (
    <Field
      label={t('cases.legPage.directionTemplate')}
      hint={t('cases.legPage.directionTemplateHint')}
    >
      {(props) => (
        <Select
          value={value}
          onChange={(id) =>
            onChange(id, (directions ?? []).find((direction) => direction.id === id))
          }
          options={(directions ?? []).map((direction) => ({
            value: direction.id,
            label: direction.name,
            hint: t('settings.directions.pointCount', {
              count: direction.points.length,
            }),
          }))}
          placeholder={t('cases.legPage.directionTemplateNone')}
          {...props}
        />
      )}
    </Field>
  )
}

/* `emptyLegDraft` — the blank leg — and `legDraftForCase` — the create form's
   seed, built on it from the case (QW-8) — live beside `legDraftFrom` and
   `toLegPayload` in `api/useCase.ts`. They have to agree about what a leg is
   made of, and a component module may export only components. */

/* ── Expense ──────────────────────────────────────────────────────────────── */

export interface ExpenseDraft {
  description: string
  counterpartyName: string
  /** The directory counterparty the actual is booked to, or null when the
   *  draft names none — a new line before the pick, or an old line whose
   *  counterparty was typed before S-22d. PICKED, never typed: the server
   *  requires one on an actual (422 `vendorId: required`) and takes the name
   *  from the directory. */
  vendorId: string | null
  /** Blank means NO plan: a new cost recorded as an actual alone, or an
   *  unplanned line being amended (S-4.1). The payload then omits `planned`. */
  planned: string
  actual: string
  /** Each side has its own currency — a plan in USD is routinely invoiced in
   *  CNY. One shared field used to stamp the plan's currency on the actual and
   *  silently re-book it on every edit. */
  plannedCurrency: string
  /** `null` while the actual FOLLOWS the plan's currency, which is the state a
   *  new line starts in; a code once somebody picks one, or when the record
   *  already has an actual in its own. */
  actualCurrency: string | null
  /** «Примечание» — the actual's note (`remarks`), sent only with an actual.
   *  `null` when the reader may not see it (suppressed, or no
   *  `counterparty.read`): a note this reader cannot read is never sent, so a
   *  save cannot clear it. A string, `''` included, replaces the stored note. */
  note: string | null
}

/** What a reader has typed on the actual's side, carried across when the new
 *  sheet switches between «Без плана» and a plan it is being entered against
 *  (S-4.1 follow-ups). The description and the plan are not among them: in
 *  the link shape both are the plan's own. */
export type ExpenseCarry = Pick<
  ExpenseDraft,
  'actual' | 'actualCurrency' | 'counterpartyName' | 'vendorId' | 'note'
>

function carryOf(draft: ExpenseDraft): ExpenseCarry {
  return {
    actual: draft.actual,
    actualCurrency: draft.actualCurrency,
    counterpartyName: draft.counterpartyName,
    vendorId: draft.vendorId,
    note: draft.note,
  }
}

/** The note's length cap. The server's own limit is the 4 KiB body cap; a
 *  thousand characters of Cyrillic (two bytes each) leaves the rest of the
 *  body room under it. */
const NOTE_MAX_LENGTH = 1000

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** The five offered codes, plus any code the record already carries that is
 *  not among them. A picker never drops a stored value it does not recognise:
 *  a line booked in KZT reopened here must still say KZT, not quietly turn into
 *  the first option. A currency code is already its own label. */
function currencyOptions(...stored: Array<string | undefined>) {
  const codes: string[] = [...CURRENCIES]
  for (const code of stored) {
    if (code && !codes.includes(code)) codes.push(code)
  }
  return toOptions(codes, (currency) => currency)
}

/** What a field code means on THIS sheet, where the generic sentence would say
 *  less than the reader needs. `required` on the plan is the server's "neither
 *  figure was sent", and on the actual it is an unplanned line being cleared
 *  (finance.FoldedExpenseInput.Validate, S-4.1). Every other code reads as
 *  `fieldErrorKey` words it. */
const EXPENSE_FIELD_MESSAGES: Record<string, Record<string, ParseKeys>> = {
  planned: { required: 'cases.money.planOrActualRequired' },
  actual: { required: 'cases.money.actualRequired' },
  /* S-22d: an actual names a directory counterparty by id — none sent, or one
     the directory does not hold (deleted, or merged away, under an open
     sheet). Both under «Контрагент». */
  vendorId: {
    required: 'common.form.counterpartyPick',
    invalid: 'common.form.counterpartyUnknown',
  },
}

/** Which of the sheet's rule sets a record gets (S-4.1). ONE sheet, four
 *  shapes, decided by the record rather than by which button opened it:
 *
 *    · `new` — a plan, an actual, or both; neither is refused;
 *    · `planned` — the plan stays required, and clearing the actual removes it;
 *    · `unplanned` — the plan is a read-only dash and is never sent (the server
 *      refuses a plan added after the fact: that plan is what hid the overrun),
 *      and the actual is required, because without it the line has no figure;
 *    · `link` — a NEW actual entered against a plan that awaits one (S-4.1
 *      follow-ups): the plan's description and figure are read-only and sent
 *      as served, the actual is required, and there is nothing to delete. */
type ExpenseShape = 'new' | 'planned' | 'unplanned' | 'link'

export function ExpenseFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  onDelete,
  pending,
  error,
  failure,
  awaitingPlans = [],
  linkedPlan,
  onLinkPlan,
  carried,
}: {
  open: boolean
  onClose: () => void
  record?: ExpenseLine
  onSubmit: (values: ExpenseDraft) => void
  /** Removes the whole cost line — the plan and every actual against it, in one
   *  transaction. Absent when adding.
   *
   *  A refusal reaches the reader through `error`, with the draft still on
   *  screen. */
  onDelete?: () => void
  pending?: boolean
  /** A refusal from the server, shown above the fields with the draft intact. */
  error?: string
  /** The same refusal, raw, for its per-field codes (QW-9, E-1). A zero passes
   *  this sheet's own number check and the server refuses it as
   *  `planned: not-positive`; the banner says the save failed, and this puts
   *  the reason under the figure it is about. */
  failure?: unknown
  /** The plans still awaiting an actual, offered on a NEW line as «Плановая
   *  статья» (S-4.1 follow-ups). Without them a reader holding the invoice for
   *  a planned cost could only record it unplanned, beside its plan, and the
   *  forecast counted the cost twice. Ignored on an existing record. */
  awaitingPlans?: readonly ExpenseLine[]
  /** The plan this new actual is being entered against — the `link` shape. */
  linkedPlan?: ExpenseLine
  /** The reader chose a plan, or «Без плана» (undefined), with what they had
   *  typed on the actual's side. The HOST re-targets the sheet: a link is not
   *  an edit target, and the draft re-seeds under its own key. */
  onLinkPlan?: (plan: ExpenseLine | undefined, carried: ExpenseCarry) => void
  /** What was typed before the last switch, seeded into the next draft. */
  carried?: ExpenseCarry
}) {
  const { t } = useTranslation()
  /* Whether this reader records actual costs at all (S-22d, owner): the
     server refuses an actual's create to a caller without `counterparty.read`
     (403 `held_by: counterparty.read`), because the counterparty it must name
     is one they cannot see. Sales keeps the sheet for PLANS; the actual, its
     counterparty and its note are shown read-only with the reason. An
     affordance only — the server re-checks. */
  const { data: session } = useSession()
  const readsCounterparties = counterpartyRights(session).read
  const [draft, setDraft, touched, markTouched] = useFormDraft<ExpenseDraft>(
    open ? (record?.id ?? (linkedPlan ? `link:${linkedPlan.id}` : 'new')) : null,
    () =>
      linkedPlan && !record
        ? {
            /* The plan's own description and figure, as served: the save is
               the plan's PUT, and a plan re-typed here would be a plan
               edited by accident. Only the actual's side is the reader's. */
            ...emptyExpense,
            note: readsCounterparties ? '' : null,
            ...carried,
            description: linkedPlan.description,
            planned: linkedPlan.planned.amount,
            plannedCurrency: linkedPlan.planned.currency,
          }
        : record
        ? {
            description: record.description,
            counterpartyName: editableValue(record.counterpartyName),
            /* Seeded from the SERVER's link rather than re-derived from the
               name. Re-deriving looks equivalent and is not: a counterparty
               renamed in the directory since this line was booked no longer
               matches by text, so an edit that only touched the amount would
               post a null id and quietly unlink it.

               `|| null` because editableValue answers '' for both absent and
               suppressed, and the payload's shape is "an id or none". */
            vendorId: editableValue(record.vendorId) || null,
            /* The FIRST actual's note, as the fold serves it — withheld with
               the counterparty (AD-6), and then never sent back. */
            note:
              readsCounterparties && !isSuppressed(record.note) ? (record.note ?? '') : null,
            /* An unplanned line is served with a placeholder zero plan in the
               actual's currency. It is not a plan anybody made, so it never
               enters the draft — and a blank plan is what keeps it off the
               wire. */
            planned: record.unplanned ? '' : record.planned.amount,
            actual: record.actual?.amount ?? '',
            /* Each side from ITS OWN currency. Seeding both from the plan is
               the silent re-book this replaced. */
            plannedCurrency: record.planned.currency,
            actualCurrency: record.actual?.currency ?? null,
          }
        : { ...emptyExpense, note: readsCounterparties ? '' : null, ...carried },
  )

  /* THE LINE THIS SHEET CANNOT EDIT. A plan settled by two vendors' invoices is
     legal and AD-4 keeps it — `actualLines` is the DTO field that says so — but
     it has no single actual figure, so there is nothing for the field below to
     hold and no draft the server could accept. It refuses such a save with a
     409, and a form that collects typing the server will certainly reject is
     worse than no form: it spends the reader's attention and then loses it.

     So this state SAYS so and offers no save, rather than disabling one — a
     greyed button with no explanation is the same dead end without the sentence.
     Nothing extra is fetched to know this: the count rides the folded line. */
  const actualLines = record?.actualLines ?? 0
  if (record && actualLines > 1) {
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title={t('cases.money.editExpense')}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" size="lg" onClick={onClose} className="sm:w-auto">
              {t('common.actions.close')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2 pt-1">
          <p className="text-text-heading text-[13px] font-medium">
            {t('cases.money.severalActualsTitle')}
          </p>
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('cases.money.severalActualsDescription', {
              description: record.description,
              lines: actualLines,
            })}
          </p>
        </div>
      </Sheet>
    )
  }

  const shape: ExpenseShape = !record
    ? linkedPlan
      ? 'link'
      : 'new'
    : record.unplanned
      ? 'unplanned'
      : 'planned'

  /* The picker, on a new line only, and only while some plan awaits its
     actual. The linked plan stays among the options even once a refetch no
     longer lists it as awaiting — a colleague may have recorded its actual
     meanwhile, and a picker never drops the value it is showing. */
  const planChoices =
    linkedPlan && !awaitingPlans.some((plan) => plan.id === linkedPlan.id)
      ? [...awaitingPlans, linkedPlan]
      : awaitingPlans
  /* Not to a reader who records no actual: entering one against a plan is
     the whole of the link shape. */
  const offersPlans = readsCounterparties && !record && planChoices.length > 0
  const planOptions = [
    { value: '', label: t('cases.money.planLinkNone') },
    ...planChoices.map((plan) => ({
      value: plan.id,
      label: `${plan.description} · ${formatMoney(plan.planned.amount, plan.planned.currency)}`,
    })),
  ]

  /* The server's word on a field, under the field. Named by the folded
     endpoint's own field keys (`finance.FoldedExpenseInput.Validate`): `item` is
     the description, `vendorId` (and the older `vendorName`) the counterparty,
     and each side's currency is `planned.currency` / `actual.currency`. The sheet's own check wins where
     both speak — it is about what is typed NOW. */
  const served = fieldErrors(failure)
  const servedError = (field: string): string | undefined => {
    const code = served[field]?.[0]
    if (!code) return undefined
    return t(EXPENSE_FIELD_MESSAGES[field]?.[code] ?? fieldErrorKey(code))
  }

  const plannedBlank = draft.planned.trim() === ''
  const actualBlank = draft.actual.trim() === ''

  /* The actual this reader may not record (S-22d): a new line, or a planned
     line's first actual. An EXISTING actual's amount stays editable — the
     server saves it and keeps the counterparty, link and note it holds. */
  const actualLocked = !readsCounterparties && (!record || record.actual === undefined)
  /* Withheld from this reader, or not theirs to record: read-only, with why. */
  const counterpartyHidden = isReadOnly(record?.counterpartyName)
  const counterpartyLocked = !readsCounterparties || counterpartyHidden
  const noteLocked = draft.note === null
  const lockedReason = counterpartyHidden
    ? t('common.suppressed.reason')
    : t('cases.money.actualNeedsCounterparties')
  const noteReason = isReadOnly(record?.note) ? t('common.suppressed.reason') : lockedReason

  /* An actual names a DIRECTORY counterparty (S-22d): a new line, a plan's
     first actual, and an old line whose name was typed before the picker was
     strict all ask for a pick. The stored name stays on screen «как введено»;
     the save is refused with the reason until a directory row is chosen. Not
     asked of a reader who cannot see counterparties — their amend keeps what
     the server holds. */
  const pickRequired = readsCounterparties && !actualBlank && !draft.vendorId

  /* What is wrong with each figure as typed, before anything is sent. A new
     line with neither figure is refused UNDER THE PLAN, the first of the two
     the eye reaches; either figure alone would do. */
  const plannedProblem: ParseKeys | undefined =
    shape === 'unplanned' || shape === 'link'
      ? undefined
      : shape === 'new' && plannedBlank
        ? actualBlank
          ? 'cases.money.planOrActualRequired'
          : undefined
        : !isValidAmount(draft.planned)
          ? 'common.form.invalidNumber'
          : undefined
  // The actual does not exist until the cost lands, so an empty value is valid
  // — except on an unplanned line, where it is the only figure there is, and
  // against a chosen plan, where entering it is the whole point.
  const actualProblem: ParseKeys | undefined = actualBlank
    ? shape === 'unplanned'
      ? 'cases.money.actualRequired'
      : shape === 'link'
        ? 'common.form.required'
        : undefined
    : !isValidAmount(draft.actual)
      ? 'common.form.invalidNumber'
      : undefined

  const descriptionError =
    (touched && !draft.description.trim() ? t('common.form.required') : undefined) ??
    servedError('item')
  const plannedError =
    (touched && plannedProblem ? t(plannedProblem) : undefined) ?? servedError('planned')
  const actualError =
    (touched && actualProblem ? t(actualProblem) : undefined) ?? servedError('actual')
  const counterpartyError = servedError('vendorId') ?? servedError('vendorName')
  const plannedCurrencyError = servedError('planned.currency')
  const actualCurrencyError = servedError('actual.currency')
  const invalid =
    !draft.description.trim() || plannedProblem !== undefined || actualProblem !== undefined

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('cases.money.editExpense') : t('cases.money.addExpense')}
      pending={pending}
      blockedReason={pickRequired ? t('common.form.counterpartyPick') : undefined}
      error={error ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {offersPlans ? (
        /* First, because it decides what the rest of the sheet is: a cost
           already on the plan is entered AGAINST it, or it is a new line. */
        <Field label={t('cases.money.planLink')} hint={t('cases.money.planLinkHint')}>
          {(props) => (
            <Select
              value={linkedPlan?.id ?? ''}
              onChange={(planId) =>
                onLinkPlan?.(
                  planChoices.find((plan) => plan.id === planId),
                  carryOf(draft),
                )
              }
              options={planOptions}
              {...props}
            />
          )}
        </Field>
      ) : null}

      <Field
        label={t('cases.money.columns.description')}
        required={shape !== 'link'}
        error={descriptionError}
      >
        {(props) => (
          <Input
            value={draft.description}
            readOnly={shape === 'link'}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('cases.money.columns.counterparty')}
        error={counterpartyError}
        hint={
          counterpartyLocked
            ? lockedReason
            : pickRequired
              ? t('common.form.counterpartyPick')
              : undefined
        }
      >
        {(props) => (
          /* The directory, by id (S-22d): every actual cost belongs to one
             counterparty record, so supplier groups, balances and the
             counterparty statement add up by id rather than by spelling. All
             six categories — a cost is owed to a broker or an agent as often
             as to a haulier — and «+ Добавить контрагента» for a reader who
             may create one, selecting the new row in this draft. The quote
             costing keeps its free-text VendorPicker; this is not that. */
          <CounterpartyPicker
            value={draft.vendorId ?? ''}
            name={draft.counterpartyName}
            categories={COUNTERPARTY_CATEGORIES}
            strings={{
              placeholder: t('finance.payments.counterpartyPlaceholder'),
              search: t('finance.payments.counterpartySearch'),
              asEntered: t('finance.payments.counterpartyAsEntered'),
              inactive: t('cases.legs.carrierInactive'),
              add: t('finance.payments.counterpartyAdd'),
            }}
            disabled={counterpartyLocked}
            onChange={({ id, name }) =>
              setDraft({ ...draft, counterpartyName: name, vendorId: id || null })
            }
            {...props}
          />
        )}
      </Field>

      {/* Beside the counterparty, because it travels with the actual as the
          counterparty does — and is withheld with it (AD-6): a note can name
          a company. Sent only with an actual; `''` clears it. */}
      <Field
        label={t('cases.money.note')}
        hint={noteLocked ? noteReason : t('cases.money.noteHint')}
      >
        {(props) => (
          <Textarea
            value={draft.note ?? ''}
            readOnly={noteLocked}
            maxLength={NOTE_MAX_LENGTH}
            rows={2}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {shape === 'unplanned' ? (
        /* The plan an unplanned line does not have, said rather than hidden: a
           missing field would read as a form that forgot one. Read-only, and
           no currency beside it — there is nothing to price. */
        <Field
          label={t('cases.money.columns.planned')}
          error={plannedError}
          hint={t('cases.money.unplannedHint')}
        >
          {(props) => <Input value="—" readOnly className="asa-num" {...props} />}
        </Field>
      ) : shape === 'link' ? (
        /* The chosen plan's figure, read-only: it is sent back exactly as
           served, and changing a plan is that line's own edit. */
        <div className="flex gap-2.5">
          <Field
            label={t('cases.money.columns.planned')}
            error={plannedError}
            className="flex-1"
          >
            {(props) => (
              <Input value={draft.planned} readOnly className="asa-num" {...props} />
            )}
          </Field>
          <Field
            label={t('cases.money.plannedCurrency')}
            error={plannedCurrencyError}
            className="w-28 shrink-0"
          >
            {(props) => <Input value={draft.plannedCurrency} readOnly {...props} />}
          </Field>
        </div>
      ) : (
        <div className="flex gap-2.5">
          <Field
            label={t('cases.money.columns.planned')}
            required={shape === 'planned'}
            error={plannedError}
            hint={shape === 'new' ? t('cases.money.plannedHintNew') : undefined}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.planned}
                onChange={(e) => setDraft({ ...draft, planned: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field
            label={t('cases.money.plannedCurrency')}
            error={plannedCurrencyError}
            className="w-28 shrink-0"
          >
            {(props) => (
              <Select
                value={draft.plannedCurrency}
                /* The actual follows along while `actualCurrency` is null —
                   that is what null means — so nothing here has to move it. */
                onChange={(plannedCurrency) => setDraft({ ...draft, plannedCurrency })}
                options={currencyOptions(record?.planned.currency)}
                {...props}
              />
            )}
          </Field>
        </div>
      )}

      {/* Variance is DERIVED from planned and actual and is never entered: any
          figure computed from editable inputs recalculates itself, and no
          "recalculate" button exists anywhere in the product. */}
      <div className="flex gap-2.5">
        <Field
          label={t('cases.money.columns.actual')}
          required={shape === 'unplanned' || shape === 'link'}
          error={actualError}
          hint={
            actualLocked
              ? t('cases.money.actualNeedsCounterparties')
              : shape === 'unplanned' || shape === 'link'
                ? undefined
                : t('cases.money.actualHint')
          }
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.actual}
              readOnly={actualLocked}
              onChange={(e) => setDraft({ ...draft, actual: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('cases.money.actualCurrency')}
          error={actualCurrencyError}
          className="w-28 shrink-0"
        >
          {(props) =>
            actualLocked ? (
              <Input value={draft.actualCurrency ?? draft.plannedCurrency} readOnly {...props} />
            ) : (
              <Select
                value={draft.actualCurrency ?? draft.plannedCurrency}
                // Picking one ends the following: from here on it is the
                // reader's answer, and a change to the plan leaves it alone.
                onChange={(actualCurrency) => setDraft({ ...draft, actualCurrency })}
                options={currencyOptions(record?.actual?.currency, draft.plannedCurrency)}
                {...props}
              />
            )
          }
        </Field>
      </div>

      {/* No «Оплачено» box (S-4.2). It set a flag nothing kept true — a line
          could read paid with no payment against it, and the payables left it
          out. Whether a cost is paid is now derived by the server from the
          payments allocated to it, and the money table says it beside the
          line's name. */}
      {onDelete ? (
        <DeleteRow
          onDelete={onDelete}
          pending={pending}
          resetKey={open ? (record?.id ?? 'new') : null}
          label={t('cases.money.deleteExpense')}
          confirm={t('cases.money.deleteExpenseConfirm')}
          confirmAction={t('cases.money.deleteExpenseConfirmAction')}
        />
      ) : null}
    </FormSheet>
  )
}

const emptyExpense: ExpenseDraft = {
  description: '',
  counterpartyName: '',
  vendorId: null,
  planned: '',
  actual: '',
  plannedCurrency: 'USD',
  actualCurrency: null,
  note: '',
}
